import { FC, useRef, useEffect, useState, useMemo, useCallback } from 'react';
import type { FinancialHealthStats } from '../../types/financialStats';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { format, parseISO } from 'date-fns';
import { ChartOverlayWrapper } from './ChartOverlayWrapper';
import ChartHeader from './ChartHeader';
import { useChartDrag, withDragHandlers } from './ChartDragUtils';
import { useEvidenceModal } from '@/context/EvidenceContext';

// Register required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface DebtToIncomeChartProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  onExpandClick?: React.MutableRefObject<any>;
  monthsToShow?: number;
  /** BUG-UI-071A: Run ID for evidence modal */
  runId?: string;
  /** BUG-UI-071A: Member ID for historical evidence */
  memberId?: string;
  /** BUG-UI-071A: Member name for modal display */
  memberName?: string;
}

// DTI thresholds
const DTI_THRESHOLDS = {
  GOOD: 36,
  FAIR: 43,
  POOR: 50,
} as const;

// Status colors
const STATUS_COLORS = {
  Good: '#10B981', // emerald-500 - good (< 36%)
  Fair: '#F59E0B', // amber-500 - moderate (36-43%)
  High: '#FB923C', // orange-400 - higher risk (44-49%)
  Poor: '#EF4444', // red-500 - high risk (50%+)
} as const;

// Helper function to determine DTI status
const getDTIStatus = (dti: number) => {
  if (dti < DTI_THRESHOLDS.GOOD) return { label: 'Good', color: STATUS_COLORS.Good };
  if (dti < DTI_THRESHOLDS.FAIR) return { label: 'Fair', color: STATUS_COLORS.Fair };
  if (dti < DTI_THRESHOLDS.POOR) return { label: 'High', color: STATUS_COLORS.High };
  return { label: 'Poor', color: STATUS_COLORS.Poor };
};

// Background bands plugin
const backgroundBandsPlugin = {
  id: 'dtiBackgroundBands',
  beforeDraw: (chart: any) => {
    const { ctx, chartArea, scales } = chart;
    const { bottom, left, right } = chartArea;
    const width = right - left;
    const top = chartArea.top;
    
    // Only draw if we have valid dimensions
    if (!width || !scales.y) return;
    
    // Calculate positions for threshold lines
    const goodY = scales.y.getPixelForValue(DTI_THRESHOLDS.GOOD);
    const fairY = scales.y.getPixelForValue(DTI_THRESHOLDS.FAIR);
    const poorY = scales.y.getPixelForValue(DTI_THRESHOLDS.POOR);
    
    ctx.save();
    
    // We're removing the background color bands
    
    // Draw threshold lines
    ctx.lineWidth = 1.5;
    const safeRight = right - 5;
    
    // Good threshold line (36%)
    ctx.strokeStyle = STATUS_COLORS.Good;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(left, goodY);
    ctx.lineTo(safeRight, goodY);
    ctx.stroke();
    
    // Fair threshold line (43%)
    ctx.strokeStyle = STATUS_COLORS.Fair;
    ctx.beginPath();
    ctx.moveTo(left, fairY);
    ctx.lineTo(safeRight, fairY);
    ctx.stroke();
    
    // Poor threshold line (50%)
    ctx.strokeStyle = STATUS_COLORS.Poor;
    ctx.beginPath();
    ctx.moveTo(left, poorY);
    ctx.lineTo(safeRight, poorY);
    ctx.stroke();
    
    // Add a subtle bottom axis line for context
    ctx.strokeStyle = '#E2E8F0';
    ctx.setLineDash([]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(left, bottom);
    ctx.lineTo(safeRight, bottom);
    ctx.stroke();
    
    // Add top axis reference line (subtle)
    ctx.beginPath();
    ctx.moveTo(left, top);
    ctx.lineTo(safeRight, top);
    ctx.stroke();
    
    // Get top of chart area for positioning
    
    // Add threshold labels
    ctx.setLineDash([]);
    ctx.font = 'bold 10px Manrope, sans-serif';
    ctx.textAlign = 'right';
    
    // Good threshold label
    ctx.fillStyle = STATUS_COLORS.Good;
    ctx.fillText('36%', safeRight - 2, goodY - 5);
    
    // Fair threshold label
    ctx.fillStyle = STATUS_COLORS.Fair;
    ctx.fillText('43%', safeRight - 2, fairY - 5);
    
    // Poor threshold label
    ctx.fillStyle = STATUS_COLORS.Poor;
    ctx.fillText('50%', safeRight - 2, poorY - 5);
    
    // Add '0%' at the bottom for context
    ctx.fillStyle = '#94A3B8';
    ctx.fillText('0%', safeRight - 2, bottom - 5);
    
    ctx.restore();
  }
};

export const DebtToIncomeChart: FC<DebtToIncomeChartProps> = ({
  data = [], // Default to empty array to prevent errors
  showDebug = false,
  onExpandClick,
  monthsToShow = 24, // Default to 24 months to match VeraScore chart
  runId,
  memberId,
  memberName,
}) => {
  const chartRef = useRef<ChartJS>(null);

  // BUG-UI-071A: Use evidence modal instead of ChartExpandModal
  const { showEvidence } = useEvidenceModal();

  // BUG-UI-071A: Open evidence modal with 4-tab structure
  const openEvidenceModal = useCallback(() => {
    if (runId) {
      if (import.meta.env.DEV) console.log('Opening EvidenceModal from DebtToIncomeChart');
      showEvidence({
        uiFieldPath: 'lender.current_finances.dti_ratio',
        runId,
        memberId,
        disclosureLevel: 'LENDER',
        memberName,
      });
    } else {
      if (import.meta.env.DEV) console.warn('DebtToIncomeChart: Cannot open evidence modal - no runId provided');
    }
  }, [runId, memberId, memberName, showEvidence]);
  
  // Ensure data is non-null and sorted chronologically
  const sortedData = useMemo(() => {
    const safeData = Array.isArray(data) ? data : [];
    return [...safeData].sort((a, b) => {
      return new Date(a.month).getTime() - new Date(b.month).getTime();
    });
  }, [data]);
  
  // Initial state for visible data (show last N months)
  const [visibleData, setVisibleData] = useState<FinancialHealthStats[]>(() => {
    // Make sure we handle empty data safely
    if (!sortedData.length) return [];
    
    // Get the most recent months up to monthsToShow
    const initialVisibleData = sortedData.slice(-monthsToShow);
    return initialVisibleData;
  });

  const color = '#0EA5E9'; // sky-600

  // Enhanced handleDataChange with animation management
  const handleDataChange = useCallback((newVisibleData: FinancialHealthStats[]) => {
    const chart = chartRef.current;
    if (chart) {
      // Store original animation settings
      const originalAnimation = chart.options.animation;
      
      // Disable animations temporarily
      // @ts-ignore
      chart.options.animation = false;
      
      // Update the chart with 'none' mode for instant update
      chart.update('none');
      
      // Update state after chart is updated
      setVisibleData(newVisibleData);
      
      // Re-enable animations after a short delay
      setTimeout(() => {
        if (chart) {
          // @ts-ignore
          chart.options.animation = originalAnimation;
        }
      }, 100);
    } else {
      setVisibleData(newVisibleData);
    }
  }, []);

  // Use the chart drag hook for consistent drag behavior
  const {
    containerRef,
    isDragging,
    viewWindow,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    goEarlier,
    goLater
  } = useChartDrag(sortedData, monthsToShow, handleDataChange);

  // Get event handlers for drag functionality
  const dragHandlers = withDragHandlers({
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    isDragging
  });

  // BUG-UI-071A: Expose the openModal function through the onExpandClick prop
  useEffect(() => {
    if (onExpandClick) {
      // @ts-ignore
      onExpandClick.current = onExpandClick.current || {};
      // @ts-ignore
      onExpandClick.current.openModal = openEvidenceModal;
    }
  }, [onExpandClick, openEvidenceModal]);

  // Only create chart data if we have visible data
  const chartData = useMemo(() => {
    // Calculate trend line
    const calculateTrendLine = (data: number[]): number[] => {
      if (data.length <= 2) return data;
      
      // Use linear regression to calculate trend line
      let sumX = 0;
      let sumY = 0;
      let sumXY = 0;
      let sumXX = 0;
      const n = data.length;
      
      // Calculate sums for linear regression formula
      for (let i = 0; i < n; i++) {
        sumX += i;
        sumY += data[i];
        sumXY += i * data[i];
        sumXX += i * i;
      }
      
      // Calculate slope and y-intercept
      const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
      const intercept = (sumY - slope * sumX) / n;
      
      // Generate trend line points
      return data.map((_, i) => intercept + slope * i);
    };
    
    const dtiValues = visibleData.map(d => d.dti_ratio != null ? Number(d.dti_ratio) : 0);
    const trendLineData = calculateTrendLine(dtiValues);
    
    // Create a single dataset with the DTI values
    return {
      labels: visibleData.map(d => d.month || ''),
      datasets: [
        {
          label: 'DTI Ratio',
          data: dtiValues,
          borderColor: color,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: 'rgba(255, 255, 255, 0.9)',
          pointBorderColor: color,
          pointBorderWidth: 2,
          pointHitRadius: 10,
        },
        // Add trend line
        {
          label: 'Trend',
          data: trendLineData,
          fill: false,
          tension: 0.2, // Slight curve for trend line
          borderWidth: 2, // Slightly thicker line
          borderDash: [6, 4], // More visible dash pattern
          pointRadius: 0,
          borderColor: 'rgba(107, 114, 128, 0.8)', // Gray color for trend line
          spanGaps: false,
        }
      ],
    };
  }, [visibleData, color]);

  // Define renderOverlay function for the modal as inline function in the ChartExpandModal
  const renderDTIOverlay = (modalData: FinancialHealthStats[]) => {
    if (modalData.length === 0) return null;
    
    const latestData = modalData[modalData.length - 1];
    const dtiRatio = latestData.dti_ratio ?? 0;
    const status = getDTIStatus(dtiRatio);
    
    return (
      <div 
        className="absolute left-16 top-2 px-2 py-1 rounded-md bg-white/90 border border-gray-200 shadow-sm max-w-[220px]"
        style={{ zIndex: 1 }}
      >
        <div className="mb-2 pb-1 border-b border-gray-100">
          <div className="text-sm text-slate-500 font-medium">Latest DTI Ratio</div>
          <div className="flex items-center gap-2 mt-1">
            <div className="text-2xl font-bold" style={{ color: status.color }}>
              {dtiRatio.toFixed(1)}%
            </div>
            <div
              className="text-xs font-medium px-1.5 py-0.5 rounded-full"
              style={{
                backgroundColor: `${status.color}20`,
                color: status.color
              }}
            >
              {status.label}
            </div>
          </div>
        </div>
        <div className="text-xs text-slate-500 leading-relaxed">
          Your debt-to-income ratio compares your monthly debt payments to your income. A lower ratio suggests greater financial stability.
        </div>
      </div>
    );
  };
  void renderDTIOverlay; // Reserved for ChartExpandModal integration

  return (
    <div className="relative h-full flex flex-col">
      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && showDebug && (
        <div className="absolute top-0 right-0 bg-white/80 p-2 text-xs text-slate-500 z-10 rounded border">
          <div>Total months: {sortedData.length}</div>
          <div>Visible: {visibleData.length}</div>
          <div>Window: {viewWindow.start} - {viewWindow.end}</div>
        </div>
      )}
      
      {/* Chart header with months shown and navigation buttons */}
      <ChartHeader
        viewWindow={viewWindow}
        totalMonths={sortedData.length}
        monthsToShow={monthsToShow}
        onEarlierClick={goEarlier}
        onLaterClick={goLater}
      />
      
      {/* Glassmorphic chart container */}
      <div 
        ref={containerRef}
        className="p-0 rounded-xl overflow-hidden w-full flex-grow"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(8px)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.8)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.03), 0 1px 6px rgba(0, 0, 0, 0.02)',
          height: 'calc(100% - 40px)', // Subtract space for x-axis labels
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          ...(dragHandlers.style || {})
        }}
        onTouchStart={dragHandlers.onTouchStart}
        onTouchMove={dragHandlers.onTouchMove}
        onTouchEnd={dragHandlers.onTouchEnd}
        onMouseDown={dragHandlers.onMouseDown}
        onMouseMove={dragHandlers.onMouseMove}
        onMouseUp={dragHandlers.onMouseUp}
        onMouseLeave={dragHandlers.onMouseLeave}
      >
        <div className="relative h-full pb-6">
          <ChartOverlayWrapper 
            overlay={null} 
            showStatsButton={false}
          >
            <Chart
              ref={chartRef}
              type="line"
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                animation: {
                  duration: 300
                },
                plugins: {
                  legend: {
                    display: false,
                  },
                  // Add the background bands plugin
                  // @ts-ignore
                  dtiBackgroundBands: {},
                  tooltip: {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    titleColor: '#334155',
                    titleFont: {
                      size: 14,
                      weight: 600,
                      family: "'Manrope', sans-serif",
                    },
                    bodyColor: '#64748B',
                    bodyFont: {
                      size: 13,
                      family: "'Manrope', sans-serif",
                    },
                    padding: 12,
                    borderColor: '#E2E8F0',
                    borderWidth: 1,
                    cornerRadius: 8,
                    callbacks: {
                      title: (tooltipItems: any) => {
                        try {
                          if (!tooltipItems?.[0]?.label) return '';
                          const label = tooltipItems[0].label;
                          const date = parseISO(label);
                          return format(date, 'MMMM yyyy');
                        } catch (e) {
                          return tooltipItems?.[0]?.label || '';
                        }
                      },
                      label: (context: any) => {
                        // Find the first non-null value from any dataset for this index
                        const index = context.dataIndex;
                        let value: number | null = null;
                        chartData.datasets.forEach(dataset => {
                          if (value === null && dataset.data[index] !== null) {
                            value = dataset.data[index] as number;
                          }
                        });
                        
                        if (value === null) return ['No data available'];
                        
                        const status = getDTIStatus(value);
                        return [
                          `DTI: ${Number(value).toFixed(1)}%`,
                          `Status: ${status.label}`
                        ];
                      },
                    }
                  }
                },
                interaction: {
                  intersect: false,
                  mode: 'nearest' as const,
                  axis: 'x' as const,
                },
                scales: {
                  x: {
                    grid: {
                      display: false,
                    },
                    ticks: {
                      font: {
                        size: 11,
                        family: "'Manrope', sans-serif",
                      },
                      color: '#64748B',
                      maxRotation: 45,
                      minRotation: 45,
                      callback: function(_value: any, index: any) {
                        try {
                          const month = visibleData[index]?.month;
                          if (!month) return '';
                          const date = parseISO(month);
                          return format(date, 'MMM yyyy');
                        } catch (e) {
                          return '';
                        }
                      }
                    },
                  },
                  y: {
                    position: 'left' as const,
                    min: 0,
                    max: 100, // Changed from 75 to 100 to allow scale from 0% to 100%
                    grid: {
                      color: '#E2E8F010', // Very subtle grid
                    },
                    ticks: {
                      font: {
                        size: 11,
                        family: "'Manrope', sans-serif",
                      },
                      color: '#64748B',
                      callback: function(value: string | number) {
                        return `${value}%`;
                      },
                      stepSize: 10,
                    },
                    border: {
                      display: false,
                    },
                  },
                },
              }}
              plugins={[backgroundBandsPlugin]}
            />
          </ChartOverlayWrapper>
        </div>
      </div>

      {/* BUG-UI-071A: ChartExpandModal removed - now using EvidenceModal via openEvidenceModal */}
    </div>
  );
};

export default DebtToIncomeChart;
